import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { ascending, merge } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import { timeDay, timeHour, timeMinute } from 'd3-time';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import AGPGraph from './AGPGraph';
import BaseSVG, { zeroMargins } from './BaseSVG';
import D3Component from './D3Component';
import GlucoseSVG from './GlucoseSVG';
import MainView from './MainView';
import NutritionPhotos from './NutritionPhotos';
import NutritionSettingContext, { getTimeFragment } from './NutritionSettingsContext';
import PresentationContext from '../PresentationContext';
import XAxis from './XAxis';
import { parseDate, urlDateFormat } from '../utils/formats';
import { adjustLayout } from '../utils/layoutUtils';
import './Dashboard.css';

const padTo2 = n => `${n >= 10 ? '' : '0'}${n}`.substr(0,2);
const fractionToTime = f => `${Math.floor(+f)}:${padTo2((f % 1) * 60)}`;
/*
function fractionToTime(f, formatTimeString) {
  const hours = Math.floor(+f);
  const minutes = (f % 1) * 60;
  return formatTimeString(new Date(0, 0, 0, hours, minutes, 0));
}
*/

function NutritionReport(props) {
  const {
    mealTimes,
    selectedMealTime,
    selectMealTime,
    setMealTimes,
  } = useContext(NutritionSettingContext);
  const {
    formatDateRange,
    formatGlucoseValue,
    formatTimeString,
    locale,
    shortHour,
  } = useContext(PresentationContext);
  const location = useLocation();
  const {
    days,
    interactive = true,
    nutritionData = {},
    percentile,
    photosData = {},
    sgvData = {},
  } = props;
  const { t } = useTranslation('dashboard');

  let firstDay = parseDate(Object.keys(sgvData).sort()[0]);
  let endOfDay = timeDay.offset(firstDay, 1);
  if (timeHour.count(firstDay, endOfDay) !== 24) {
    // daylight saving time change...
    // let's take another day
    firstDay = timeDay.offset(firstDay, 1);
    endOfDay = timeDay.offset(firstDay, 1);
  }
  const times = [0, endOfDay];

  const filteredPhotos = useMemo(() => merge(Object.values(photosData)).filter((p) => {
    if (isNaN(selectedMealTime)) {
      return true;
    }
    const d = new Date(p.time);
    const f = getTimeFragment(d);
    const m = mealTimes[selectedMealTime];
    if (!m) {
      // A meal time has been selected but no longer exists...
      return false;
    }
    return f >= m.start && f <= m.end;
  }), [mealTimes, photosData, selectedMealTime]);

  const filteredMealEffects = useMemo(() => merge(Object.values(nutritionData)).filter((n) => {
    if (isNaN(selectedMealTime)) {
      return true;
    }
    const d = new Date(n.time);
    const f = getTimeFragment(d);
    const m = mealTimes[selectedMealTime];
    if (!m) {
      // A meal time has been selected but no longer exists...
      return false;
    }
    return f >= m.start && f <= m.end;
  }).map(n => n.time).sort(ascending),
    [mealTimes, nutritionData, selectedMealTime]);

  const filteredSgv = filteredMealEffects.reduce((o, m) => {
    const date = new Date(m);
    const day = urlDateFormat(date);
    const end = timeHour.offset(date, 4);
    const endString = end.toISOString();
    const startOfDay = timeDay(date);
    const minuteOffset = timeMinute.count(startOfDay, date);
    const nextDay = minuteOffset > 1200 ? (sgvData[urlDateFormat(end)] || []) : [];
    o[day] = [...(sgvData[day] || []), ...nextDay]
      .filter(s => s.time >= m && s.time <= endString)
      .map(s => ({ ...s, time: timeMinute.offset(new Date(s.time), -minuteOffset).toISOString() }));
    return o;
  }, {});

  const epochHours = [new Date(0), new Date(4 * 60 * 60 * 1000)];

  // Make adjustable:
  // - every hour on bigger screens,
  // - only even hours on medium size screens,
  // - every third on small screens?
  /*
  const mealTimeHours = useMemo(() => mealTimes.reduce((arr, time) => {
    arr.push(Math.round(time.start));
    arr.push(Math.round(time.end));
    return arr;
  }, [mealTimes]));
  */

  const adjustmentScale = scaleLinear().domain([0,24]).range([0,600]);

  function adjustMealTime(meal, position, time) {
    const newMealTimes = [...mealTimes];
    const constrainedTime = Math.max(0, Math.min(time, 24));
    newMealTimes[meal][position] = constrainedTime;
    if (position === 'end') {
      newMealTimes[meal].start = Math.min(newMealTimes[meal].start, constrainedTime);
    } else {
      newMealTimes[meal].end = Math.max(newMealTimes[meal].end, constrainedTime);
    }
    for (let i = 0; i < meal; i += 1) {
      newMealTimes[i].start = Math.min(newMealTimes[i].start, constrainedTime);
      newMealTimes[i].end = Math.min(newMealTimes[i].end, constrainedTime);
    }
    for (let i = +meal + 1; i < newMealTimes.length; i += 1) {
      newMealTimes[i].start = Math.max(newMealTimes[i].start, constrainedTime);
      newMealTimes[i].end = Math.max(newMealTimes[i].end, constrainedTime);
    }
    setMealTimes(newMealTimes);
  }

  const dragListener = useRef(undefined);

  function registerDragSlider(slider, event) {
    const initialX = event.clientX;
    let busy = false;
    const svg = event.target.ownerSVGElement;
    const scale = (svg.viewBox.baseVal.width || 650) / svg.clientWidth;
    let moved = 0;
    const meal = +slider[4];
    const position = slider.substr(5);
    const initialTime = mealTimes[meal][position];

    dragListener.current = (event) => {
      const pointerEvent = event;
      if (busy) {
        return;
      }
      busy = true;
      const move = ((pointerEvent.clientX - initialX) * scale) - moved;
      window.setTimeout(() => {
        // const newMealTimes = [ ...mealTimes ];
        // calculate how to adjust the hidden input!
        moved += move;
        const preciseTime = initialTime + adjustmentScale.invert(moved);
        // Round to 15 minutes, that is to .25 in a number
        const rounded = Math.round(preciseTime * 4) / 4;
        adjustMealTime(meal, position, rounded);
        busy = false;
      }, 0);
    }
    document.body.addEventListener('pointermove', dragListener.current);
    document.body.addEventListener('pointerup', releaseDragSlider);
    document.body.addEventListener('pointerleave', releaseDragSlider);
  }

  function releaseDragSlider() {
    document.body.removeEventListener('pointermove', dragListener.current);
    dragListener.current = undefined;
  }

  const keyListener = useRef(undefined);

  function registerKeyListener(slider, target) {
    const meal = +slider[4];
    const position = slider.substr(5);
    const minute = 1/60;

    keyListener.current = (event) => {
      const { synthEvent } = event || {};
      const myEvent = synthEvent || event;
      if (myEvent.keyCode >= 48 && myEvent.keyCode <= 57) {
        // TODO: allow editing the time
      } else if (myEvent.code === 'ArrowLeft') {
        myEvent.preventDefault();
        myEvent.stopPropagation();
        // adjustMealTime(meal, position, mealTimes[meal][position] - minute);
        const newTime = mealTimes[meal][position] - (minute * (myEvent.ctrlKey ? 1 : (myEvent.shiftKey ? 60 : 15)));
        adjustMealTime(meal, position, newTime);
      } else if (event.code === 'ArrowRight') {
        myEvent.preventDefault();
        myEvent.stopPropagation();
        const newTime = mealTimes[meal][position] + (minute * (myEvent.ctrlKey ? 1 : (myEvent.shiftKey ? 60 : 15)));
        adjustMealTime(meal, position, newTime);
      }
    };
    select(target).on('keydown.adjustMealTime', keyListener.current);
    // document.body.addEventListener('keydown', keyListener.current);
    // This fights with the days?? Dys gets a preference?
    // Rather add to the focused element?
  }

  function releaseKeyListener(target) {
    // document.body.removeEventListener('keydown', keyListener.current);
    select(target).on('keydown.adjustMealTime', null);
    keyListener.current = undefined;
  }


  useEffect(() => {
    const layoutSelection = select('#mealTimeAdjustment');
    adjustLayout(layoutSelection);
  }, [mealTimes]);

  const mealTimeTickFormat = useMemo(() => {
    return function(d, i, a) {
      let w = 500;
      try {
        w =  select(this).node().ownerSVGElement.width.baseVal.value;
      } catch {
        // OK, not every renderer reports the SVG width this way (especially the test renderer)
        // let's just use some value.
        w = 500;
      }
      // const showEveryNthHour = (w > 850) ? 1 : ((w > 600) ? 2 : 3);
      const showEveryNthHour = (w > 1000) ? 3 : 4;
      if (d.getMinutes() === 0) {
        // The last hour should count as 24...
        const h = d.getHours() + ((i === (a.length - 1)) ? 24 : 0);
        if ((h % showEveryNthHour) === 0) {
          if (!mealTimes.some(meal => Object.values(meal).some(
            time => (time > (h - showEveryNthHour)) && (time < (h + showEveryNthHour))
          ))) {
            return formatTimeString(d);
          }
        }
      }
      return undefined;
    };
  }, [formatTimeString, mealTimes]);

  console.log('Filtered meals', { filteredSgv });

  return (
    <MainView
      description={t('nutrition.description')}
      heading={t('nutrition.heading')}
      interactive={interactive}
      key="mainView"
      times={times}
      title={`Sensotrend Nutrition Report ${formatDateRange(days)}`}
    >
      <section id="nutrition">
        <form id="mealTimeAdjust">
          {mealTimes.map((time, i) => (
            <fieldset key={i}>
              <legend>{t('nutrition.meal', { number: i + 1})}</legend>
              <label htmlFor={`meal${i}start`}>{t('nutrition.start')}</label>
              <input
                type="range"
                id={`meal${i}start`}
                min="0"
                max="24"
                step="0.5"
                value={time.start}
                onChange={(event) => {
                  adjustMealTime(i, 'start', event.target.value);
                }}
              />
              <label htmlFor={`meal${i}end`}>{t('nutrition.end')}</label>
              <input
                type="range"
                id={`meal${i}end`}
                min="0"
                max="24"
                step="0.5"
                value={time.end}
                onChange={(event) => {
                  adjustMealTime(i, 'end', event.target.value);
                }}
              />
            </fieldset>
          ))}
        </form>
        <svg id="mealTimeAdjustment" viewBox="-25 0 650 60" shapeRendering="crispEdges">
          <defs>
            <g
              id="slider"
            >
              <rect x="-5" y="-10" width="10" height="20" />
              <line className="grip" x1="-2.5" x2="-2.5" y1="-7" y2="7" />
              <line className="grip" x1="0" x2="0" y1="-7" y2="7" />
              <line className="grip" x1="2.5" x2="2.5" y1="-7" y2="7" />
            </g>
          </defs>

          <XAxis
            width={600}
            y={30}
            tickFormat={mealTimeTickFormat}
            ticks={49}
          />
          <line className="track" x1="0" x2="601" y1="25" y2="25" />
          {mealTimes.map((time, i) => (
            <g
              key={i}
              className={`mealTime${i === +selectedMealTime
                ? ' selected'
                : (selectedMealTime ? ' dimmed' : '')
              }`}
            >
              <Link
                to={{ ...location, search: ``}}
                onClick={(event) => {
                  event.preventDefault();
                  selectMealTime(i === +selectedMealTime ? undefined : i);
                }}
              >
                <text
                  className="label"
                  x={adjustmentScale(time.start) + (adjustmentScale(time.end) - adjustmentScale(time.start)) / 2}
                  y="12.5"
                >
                  {t('nutrition.meal', { number: i + 1})}
                </text>
                <line
                  className="bar"
                  x1={adjustmentScale(time.start)}
                  x2={adjustmentScale(time.end)}
                  y1="25"
                  y2="25"
                />
              </Link>
              <use
                href="#slider"
                x={adjustmentScale(time.start)}
                y="25"
                onMouseDown={(event) => registerDragSlider(`meal${i}start`, event)}
                onMouseUp={() => releaseDragSlider()}
              />
              <text
                className="axis-text adjustLayout"
                x={adjustmentScale(time.start)}
                y="39"
                dy="0.71em"
                onFocus={(event) => registerKeyListener(`meal${i}start`, event.target)}
                onBlur={(event) => releaseKeyListener(event.target)}
                tabIndex="0"
              >
                {fractionToTime(time.start)}
              </text>
              <use
                href="#slider"
                x={adjustmentScale(time.end)}
                y="25"
                onMouseDown={(event) => registerDragSlider(`meal${i}end`, event)}
                onMouseUp={() => releaseDragSlider()}
              />
              <text
                className="axis-text adjustLayout"
                x={adjustmentScale(time.end)}
                y="39"
                dy="0.71em"
                onFocus={(event) => registerKeyListener(`meal${i}end`, event.target)}
                onBlur={(event) => releaseKeyListener(event.target)}
                tabIndex="0"
              >
                {fractionToTime(time.end)}
              </text>
            </g>
          ))}
          </svg>
        <GlucoseSVG
          key="mainGraph"
          times={epochHours}
          tickFormat={(t) => {
            const h = t.getUTCHours();
            return h ? `+${h} ${shortHour}` : '';
          }}
        >
          <D3Component
            graph={AGPGraph}
            data={filteredSgv}
            formatGlucoseValue={formatGlucoseValue}
            hours={[0, 1, 2, 3]}
            locale={locale}
            percentile={percentile}
            t={t}
          />
        </GlucoseSVG>
      </section>
      <section>
        <NutritionPhotos key="firstAdditionalGraph" photos={filteredPhotos} />
      </section>
      <section>
        <BaseSVG
          key="secondAdditionalGraph"
          height={0}
          svgWidth={0}
          svgHeight={0}
          margins={zeroMargins}
          showXAxis={false}
        />
      </section>
    </MainView>
  );
}

export default NutritionReport;
